import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';
import { from, identity } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, groupBy, map, mergeMap, switchMap } from 'rxjs/operators';

import { MAIN_NAVIGATION_MAX_SUB_CATEGORIES_DEPTH } from 'ish-core/configurations/injection-keys';
import { CategoryHelper } from 'ish-core/models/category/category.model';
import { ofCategoryUrl } from 'ish-core/routing/category/category.route';
import { CategoriesService } from 'ish-core/services/categories/categories.service';
import { selectRouteParam, selectRouteParamAorB } from 'ish-core/store/core/router';
import { setBreadcrumbData } from 'ish-core/store/core/viewconf';
import { personalizationStatusDetermined } from 'ish-core/store/customer/user';
import { loadMoreProducts } from 'ish-core/store/shopping/product-listing';
import { HttpStatusCodeService } from 'ish-core/utils/http-status-code/http-status-code.service';
import { InjectSingle } from 'ish-core/utils/injection';
import {
  delayUntil,
  mapErrorToAction,
  mapToPayload,
  mapToPayloadProperty,
  useCombinedObservableOnAction,
  whenTruthy,
} from 'ish-core/utils/operators';

import {
  fetchSpecificCategory,
  loadCategory,
  loadCategoryByRef,
  loadCategoryFail,
  loadCategoryLinks,
  loadCategoryLinksFail,
  loadCategoryLinksSuccess,
  loadCategorySuccess,
  loadCategoryTree,
  loadCategoryTreeSuccess,
  loadSingleCategoryByRef,
  loadSingleCategoryByRefFail,
  loadSingleCategoryByUniqueId,
  loadSingleCategoryByUniqueIdFail,
  loadTopLevelCategories,
  loadTopLevelCategoriesFail,
  loadTopLevelCategoriesSuccess,
} from './categories.actions';
import {
  getBreadcrumbForCategoryPage,
  getCategoryEntities,
  getCategoryRefs,
  getSelectedCategory,
} from './categories.selectors';

@Injectable()
export class CategoriesEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private categoryService: CategoriesService,
    @Inject(MAIN_NAVIGATION_MAX_SUB_CATEGORIES_DEPTH)
    private mainNavigationMaxSubCategoriesDepth: InjectSingle<typeof MAIN_NAVIGATION_MAX_SUB_CATEGORIES_DEPTH>,
    private httpStatusCodeService: HttpStatusCodeService
  ) {}

  /**
   * listens to routing and fires {@link LoadCategory}
   * when the requested {@link Category} is not available, yet
   */
  selectedCategory$ = createEffect(() =>
    this.actions$.pipe(
      useCombinedObservableOnAction(
        this.store.pipe(select(selectRouteParam('categoryUniqueId'))),
        personalizationStatusDetermined
      ),
      whenTruthy(),
      concatLatestFrom(() => this.store.pipe(select(getCategoryEntities))),
      filter(([id, entities]) => !CategoryHelper.isCategoryCompletelyLoaded(entities[id])),
      map(([categoryId]) => loadCategory({ categoryId }))
    )
  );

  /**
   * listens to routing and fires {@link loadCategoryByRef}
   * when the requested ref in {@link getCategoryRefs} is not available, yet
   */
  selectedCategoryRef$ = createEffect(() =>
    this.actions$.pipe(
      useCombinedObservableOnAction(
        this.store.pipe(select(selectRouteParam('categoryRefId'))),
        personalizationStatusDetermined
      ),
      whenTruthy(),
      concatLatestFrom(() => [this.store.pipe(select(getCategoryRefs)), this.store.pipe(select(getCategoryEntities))]),
      filter(
        ([id, refs, entities]) =>
          !refs[id] || (refs[id] && !CategoryHelper.isCategoryCompletelyLoaded(entities[refs[id]]))
      ),
      map(([categoryId]) => loadCategoryByRef({ categoryId }))
    )
  );

  /**
   * loads a {@link Category} using the {@link CategoriesService}
   */
  loadCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCategory, loadCategoryByRef),
      mapToPayloadProperty('categoryId'),

      mergeMap(id =>
        this.categoryService.getCategory(id).pipe(
          map(categories => loadCategorySuccess({ categories })),
          mapErrorToAction(loadCategoryFail)
        )
      )
    )
  );

  fetchSpecificCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchSpecificCategory),
      mapToPayloadProperty('categoryId'),
      concatLatestFrom(() => this.store.pipe(select(getCategoryEntities))),
      filter(([id, entities]) => !CategoryHelper.isCategoryCompletelyLoaded(entities[id])),
      map(([categoryId]) => loadCategory({ categoryId }))
    )
  );

  loadTopLevelCategories$ = createEffect(() =>
    this.actions$.pipe(
      useCombinedObservableOnAction(
        this.actions$.pipe(ofType(loadTopLevelCategories)),
        personalizationStatusDetermined
      ),
      switchMap(() =>
        this.categoryService.getTopLevelCategories(SSR ? 0 : this.mainNavigationMaxSubCategoriesDepth).pipe(
          map(categories => loadTopLevelCategoriesSuccess({ categories })),
          mapErrorToAction(loadTopLevelCategoriesFail)
        )
      )
    )
  );

  loadCategoryTree$ = createEffect(() =>
    this.actions$.pipe(
      useCombinedObservableOnAction(this.actions$.pipe(ofType(loadCategoryTree)), personalizationStatusDetermined),
      mapToPayload(),
      switchMap(({ categoryRef, depth }) =>
        this.categoryService
          .getCategoryTree(categoryRef, depth)
          .pipe(map(categories => loadCategoryTreeSuccess({ categories })))
      )
    )
  );

  productOrCategoryChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      switchMap(() =>
        this.store.pipe(
          ofCategoryUrl(),
          select(getSelectedCategory),
          whenTruthy(),
          filter(cat => cat.hasOnlineProducts),
          distinctUntilChanged((cat1, cat2) => cat1.uniqueId === cat2.uniqueId),
          map(({ uniqueId }) => loadMoreProducts({ id: { type: 'category', value: uniqueId } }))
        )
      )
    )
  );

  redirectIfErrorInCategories$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadCategoryFail),
        /* DIAS Customization Start */
        // only a category or product page is 'not found' because of a category - a CMS component resolving a
        // category hidden by the customer segment's catalog views must not turn e.g. the homepage into an error page
        concatLatestFrom(() => [
          this.store.pipe(select(selectRouteParam('sku'))),
          this.store.pipe(select(selectRouteParamAorB('categoryUniqueId', 'categoryRefId'))),
        ]),
        filter(([, sku, categoryId]) => !!sku || !!categoryId),
        /* DIAS Customization End */
        concatMap(() => from(this.httpStatusCodeService.setStatus(404)))
      ),
    { dispatch: false }
  );

  setBreadcrumbForCategoryPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      switchMap(() =>
        this.store.pipe(
          ofCategoryUrl(),
          select(getBreadcrumbForCategoryPage),
          whenTruthy(),
          map(breadcrumbData => setBreadcrumbData({ breadcrumbData }))
        )
      )
    )
  );

  /* DIAS Customization Start */
  /**
   * loads a main navigation {@link Category} tree by its categoryRef using the {@link CategoriesService}. The tree
   * response is stored as is - it used to be reduced to the category path and then requested a second time by
   * uniqueId. Waits for the PGID like the other category loads, as catalog views decide per customer segment
   * what the tree contains.
   */
  loadSingleCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSingleCategoryByRef),
      delayUntil(this.actions$.pipe(ofType(personalizationStatusDetermined))),
      mapToPayloadProperty('categoryId'),
      // one request per tree at a time, the newest wins - an answer still in flight for the previous PGID must not
      // overwrite the tree of the current one
      groupBy(identity),
      mergeMap(id$ =>
        id$.pipe(
          switchMap(id =>
            this.categoryService.getCategoryByRefId(id).pipe(
              map(categories => loadCategorySuccess({ categories })),
              mapErrorToAction(loadSingleCategoryByRefFail)
            )
          )
        )
      )
    )
  );
  loadSingleCategoryById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSingleCategoryByUniqueId),
      delayUntil(this.actions$.pipe(ofType(personalizationStatusDetermined))),
      mapToPayloadProperty('uniqueId'),
      /* DIAS Customization Start */
      // skip categories that are already completely loaded - e.g. hydrated from the SSR state, or requested by another
      // component. Safe for the per-PGID reload: the categories slice is reset on personalizationStatusChanged (see
      // DefaultShoppingStoreConfig), and this check runs after delayUntil, so it sees the state after that reset
      concatLatestFrom(() => this.store.pipe(select(getCategoryEntities))),
      filter(([id, entities]) => !CategoryHelper.isCategoryCompletelyLoaded(entities[id])),
      map(([id]) => id),
      // one request per category at a time, the newest wins: callers re-request on every PGID change (see
      // ShoppingFacade.getSingleCategoryByUniqueId$), several components may ask for the same category, and an
      // answer still in flight for the previous customer must not overwrite the current customer's
      groupBy(identity),
      mergeMap(id$ =>
        id$.pipe(
          switchMap(id =>
            this.categoryService.getCategory(id).pipe(
              map(categories => loadCategorySuccess({ categories })),
              mapErrorToAction(loadSingleCategoryByUniqueIdFail)
            )
          )
        )
      )
      /* DIAS Customization End */
    )
  );
  /* DIAS Customization End */

  loadCategoryLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCategoryLinks),
      mapToPayloadProperty('categoryId'),
      mergeMap(categoryId =>
        this.categoryService.getCategoryProductLinks(categoryId).pipe(
          map(links => loadCategoryLinksSuccess({ categoryId, links })),
          mapErrorToAction(loadCategoryLinksFail, { categoryId })
        )
      )
    )
  );
}
